<template>
  <OareContentView title="Edit Text Content">
    <v-card min-height="500px" class="ma-0" variant="flat">
      <v-row>
        <v-col cols="3" class="text-center">
          <side-card
            v-for="(side, idx) in sides"
            :key="idx"
            :side="side.side"
            :selected="selectedSide === side.side"
            @set-side="setSide(side.side)"
            @remove-side="removeSide(idx)"
            @change-side="setupChangeSide(idx)"
          />
          <v-divider v-if="sides.length > 0" />
          <v-btn
            v-if="usableSides.length > 0"
            @click="openAddingSide"
            class="ma-4 test-add-side"
            variant="text"
          >
            <v-icon color="primary">mdi-plus</v-icon>
            <h3 class="text-primary">Add Side</h3>
          </v-btn>
        </v-col>
        <v-col cols="1" class="px-0 text-center">
          <v-divider vertical class="ma-0" />
        </v-col>
        <v-col cols="8">
          <v-card
            v-if="!selectedSide && !addingSide && !changingSide"
            min-height="100px"
            class="py-12"
            variant="flat"
          >
            <v-row class="my-12 justify-center">
              To begin, use the "Add Side" button to select a side to start
              with. Additional sides can be added afterwards.
            </v-row>
          </v-card>
          <side
            @edited="$emit('edited')"
            @reported="restoreCounter.report(side.uuid)"
            v-show="side.side === selectedSide && !changingSide"
            v-for="(side, idx) in sortedSides"
            :key="side.uuid"
            :side="side.side"
            :startingLine="getStartingLine(idx)"
            :beginningBrokenAreas="getStartingBreak(idx)"
            :beginsWithBreak="getBeginningBreakStatus(idx)"
            :isCurrentSide="currentSide === side.uuid"
            :initialColumns="initialColumns[side.uuid]"
            @side-last-line="setLastLine(idx, $event)"
            @side-broken-area="setBrokenArea(idx, $event)"
            @side-ends-broken="setEndBreakStatus(idx, $event)"
            @side-dirty-status="setDirtyStatus(idx, $event)"
            @update-side-columns="setSideColumns(idx, $event)"
            @set-current-side="setCurrentSide(side.uuid)"
          />
          <add-side
            v-if="addingSide"
            :usableSides="usableSides"
            action="add"
            @side-selected="addSide"
            @cancel-add-side="addingSide = false"
          />
          <add-side
            v-if="changingSide"
            :usableSides="usableSides"
            action="change"
            @side-selected="changeSide"
            @cancel-add-side="changingSide = false"
          />
        </v-col>
      </v-row>
    </v-card>
  </OareContentView>
</template>

<script lang="ts">
import {
  defineComponent,
  ref,
  computed,
  ComputedRef,
  watch,
  PropType,
  onMounted,
} from 'vue';
import { v4 } from 'uuid';
import {
  EpigraphicUnitSide,
  ColumnContent,
  SideContent,
  AddTextEditorContent,
} from '@oare/types';
import Side from './components/Side.vue';
import AddSide from './components/AddSide.vue';
import SideCard from './components/SideCard.vue';
import { convertSideToSideNumber } from '@oare/oare';
import { createRestoreCounter, DraftColumn, DraftSide } from '../utils/draft';

export interface Side {
  uuid: string;
  side: EpigraphicUnitSide;
  lastLine: number;
  breaks: number;
  endsBroken: boolean;
  isDirty: boolean;
  columns: ColumnContent[];
}

export default defineComponent({
  components: {
    Side,
    AddSide,
    SideCard,
  },
  props: {
    // Sides restored from a saved draft; read once when the editor mounts.
    initialSides: {
      type: Array as PropType<DraftSide[]>,
      required: false,
    },
  },
  // edited: the user changed a row's text, as opposed to a restore.
  // restored: every restored side, column and row has reported its content.
  emits: ['update-editor-content', 'step-complete', 'edited', 'restored'],
  setup(props, { emit }) {
    const indexToChange = ref(0);
    const sides = ref<Side[]>(
      (props.initialSides || []).map(side => ({
        uuid: v4(),
        side: side.side,
        lastLine: 0,
        breaks: 0,
        endsBroken: false,
        isDirty: false,
        columns: [],
      }))
    );
    // Keyed by side uuid so a side added later never picks up a draft.
    const initialColumns: { [sideUuid: string]: DraftColumn[] } = {};
    sides.value.forEach((side, idx) => {
      initialColumns[side.uuid] = props.initialSides![idx].columns;
    });
    // Each initial side acknowledges after reporting all of its columns,
    // including empty ones. Watch readiness as well as content so an
    // acknowledgment arriving in a later flush still completes restoration.
    const allSidesReported = ref(false);
    let restoredSent = false;
    const restoreCounter = createRestoreCounter(
      sides.value.map(side => side.uuid),
      () => {
        allSidesReported.value = true;
      }
    );
    const sendRestoredAfterReport = () => {
      if (allSidesReported.value && !restoredSent && props.initialSides) {
        restoredSent = true;
        emit('restored');
      }
    };
    onMounted(restoreCounter.start);

    const selectedSide = ref<EpigraphicUnitSide | undefined>(
      sides.value[0]?.side
    );

    const addingSide = ref(false);
    const changingSide = ref(false);
    const removingSide = ref(false);

    const sideTypes: ComputedRef<EpigraphicUnitSide[]> = computed(() => [
      'obv.',
      'lo.e.',
      'rev.',
      'u.e.',
      'le.e.',
      'r.e.',
      'mirror text',
      'legend',
      'suppl. tablet',
      'obv. ii',
    ]);

    const usableSides = computed(() =>
      sideTypes.value.filter(
        type => !sides.value.map(side => side.side).includes(type)
      )
    );

    const sortSides = (a: Side, b: Side) => {
      if (a.side === 'obv.') {
        return -1;
      }
      if (a.side === 'lo.e.') {
        if (b.side === 'obv.') {
          return 1;
        }
        return -1;
      }
      if (a.side === 'rev.') {
        if (b.side === 'obv.' || b.side === 'lo.e.') {
          return 1;
        }
        return -1;
      }
      if (a.side === 'u.e.') {
        if (b.side === 'obv.' || b.side === 'lo.e.' || b.side === 'rev.') {
          return 1;
        }
        return -1;
      }
      if (a.side === 'le.e.') {
        if (
          b.side === 'obv.' ||
          b.side === 'lo.e.' ||
          b.side === 'rev.' ||
          b.side === 'u.e.'
        ) {
          return 1;
        }
        return -1;
      }
      if (a.side === 'r.e.') {
        if (
          b.side === 'mirror text' ||
          b.side === 'legend' ||
          b.side === 'suppl. tablet' ||
          b.side === 'obv. ii'
        ) {
          return -1;
        }
        return 1;
      }
      if (a.side === 'mirror text') {
        if (
          b.side === 'legend' ||
          b.side === 'suppl. tablet' ||
          b.side === 'obv. ii'
        ) {
          return -1;
        }
        return 1;
      }
      if (a.side === 'legend') {
        if (b.side === 'suppl. tablet' || b.side === 'obv. ii') {
          return -1;
        }
        return 1;
      }
      if (a.side === 'suppl. tablet') {
        if (b.side === 'obv. ii') {
          return -1;
        }
        return 1;
      }
      if (a.side === 'obv. ii') {
        return 1;
      }
      return 0;
    };

    // `sides` is kept sorted in place: the index-based handlers below rely on
    // `sides` and `sortedSides` sharing the same order.
    const sortedSides = computed(() => sides.value);

    const openAddingSide = () => {
      addingSide.value = true;
      selectedSide.value = undefined;
    };

    const addSide = (side: EpigraphicUnitSide) => {
      sides.value.push({
        uuid: v4(),
        side,
        lastLine: 0,
        breaks: 0,
        endsBroken: false,
        isDirty: false,
        columns: [],
      });
      sides.value.sort(sortSides);
      addingSide.value = false;
      selectedSide.value = side;
    };

    const removeSide = (index: number) => {
      removingSide.value = true;
      sides.value.splice(index, 1);
      if (sortedSides.value.length > 0) {
        selectedSide.value = sortedSides.value[0].side;
      } else {
        selectedSide.value = undefined;
      }
    };

    const setSide = (side: EpigraphicUnitSide) => {
      if (!changingSide.value && !removingSide.value) {
        addingSide.value = false;
        selectedSide.value = side;
      }
      removingSide.value = false;
    };

    const setLastLine = (index: number, lastLine: number) => {
      sides.value[index].lastLine = lastLine;
    };

    const getStartingLine = (index: number) => {
      if (index === 0) {
        return 0;
      }
      let startingLine = 0;
      for (let i = index - 1; i >= 0 && !startingLine; i -= 1) {
        if (sides.value[i].lastLine !== 0) {
          startingLine = sides.value[i].lastLine;
        }
      }
      return Math.floor(startingLine);
    };

    const setBrokenArea = (index: number, breaks: number) => {
      sides.value[index].breaks = breaks;
    };

    const getStartingBreak = (index: number) => {
      if (index === 0) {
        return 0;
      }
      let startingBreak = 0;
      for (let i = index - 1; i >= 0; i -= 1) {
        if (sides.value[i].breaks !== 0) {
          startingBreak += sides.value[i].breaks;
        }
      }
      return startingBreak;
    };

    const setEndBreakStatus = (index: number, status: boolean) => {
      sides.value[index].endsBroken = status;
    };

    const getBeginningBreakStatus = (index: number) => {
      if (index === 0) {
        return false;
      }
      let startingBreak = false;
      let foundDirty = false;

      for (let i = index - 1; i >= 0 && !startingBreak && !foundDirty; i -= 1) {
        if (sides.value[i].isDirty) {
          foundDirty = true;
        }
        if (sides.value[i].endsBroken) {
          startingBreak = true;
        }
      }
      return startingBreak;
    };

    const setDirtyStatus = (index: number, status: boolean) => {
      sides.value[index].isDirty = status;
    };

    const setupChangeSide = (index: number) => {
      changingSide.value = true;
      indexToChange.value = index;
    };

    const changeSide = (side: EpigraphicUnitSide) => {
      sides.value[indexToChange.value].side = side;
      sides.value.sort(sortSides);
      changingSide.value = false;
      selectedSide.value = side;
    };

    const setSideColumns = (index: number, columns: ColumnContent[]) => {
      sides.value.splice(index, 1, {
        ...sides.value[index],
        columns,
      });
    };

    watch(
      [sides, allSidesReported],
      () => {
        const sideContent: SideContent[] = sides.value.map(side => ({
          uuid: side.uuid,
          type: side.side,
          number: convertSideToSideNumber(side.side),
          columns: side.columns,
        }));
        const editorContent: AddTextEditorContent = {
          sides: sideContent,
        };
        emit('update-editor-content', editorContent);
        sendRestoredAfterReport();
      },
      { deep: true }
    );

    const stepComplete = computed(() => {
      return (
        sides.value.length > 0 &&
        sides.value.every(
          side =>
            side.columns.length > 0 &&
            side.columns.every(
              column =>
                column.rows.length > 0 &&
                column.rows.every(row => {
                  if (row.type === 'Line') {
                    return (
                      row.signs &&
                      row.signs.length > 0 &&
                      row.signs.every(sign => sign.type)
                    );
                  }
                  return true;
                }) &&
                column.rows.every(row => !row.hasErrors)
            )
        )
      );
    });

    watch(stepComplete, () => emit('step-complete', stepComplete.value));

    const currentSide = ref<string>();
    const setCurrentSide = (sideUuid?: string) => {
      currentSide.value = sideUuid;
    };

    return {
      restoreCounter,
      initialColumns,
      sides,
      addingSide,
      removingSide,
      usableSides,
      addSide,
      removeSide,
      selectedSide,
      setSide,
      openAddingSide,
      sortedSides,
      setLastLine,
      getStartingLine,
      setBrokenArea,
      getStartingBreak,
      setEndBreakStatus,
      getBeginningBreakStatus,
      setDirtyStatus,
      changingSide,
      setupChangeSide,
      changeSide,
      setSideColumns,
      currentSide,
      setCurrentSide,
    };
  },
});
</script>
